// Reception dashboard — route shell (RON-5469).
//
// OPENS IN ITS OWN TAB. The prototype is built that way — `rcClose()` calls `window.close()` and
// `rcProfile()` calls `window.opener.focus()` — and the sidebar link opens it with `target`
// rather than routing in place. So this screen renders NO app Sidebar and no house header: it is
// a full-width desk display with its own chrome.
//
// Contract (READ-ONLY — the Laravel team's folder):
//   D:\laragon\www\laravel-migration\docs\RON-5460\RON-5460_frontend-reception.md
// Prototype:
//   D:\laragon\www\visitor-management-prototype\prototypes\reception-dashboard.html
//
// Phase R1 draws the board — one `GET /visitor/reception/board` paints the whole thing. The
// detail panel, walk-in dialog, check-in/out and the PIN lock are R2-R4 and are deliberately not
// stubbed here: a reception screen with buttons that only toast is worse than one without them.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useApiUiResponse } from "../../hooks/apiUiResponseHelper.js";
import ReceptionDateBar from "./components/ReceptionDateBar.jsx";
import ReceptionFilterTabs from "./components/ReceptionFilterTabs.jsx";
import ReceptionList from "./components/ReceptionList.jsx";
import ReceptionStats from "./components/ReceptionStats.jsx";
import ReceptionTopBar from "./components/ReceptionTopBar.jsx";
import ReceptionDetailPanel from "./components/ReceptionDetailPanel.jsx";
import ReceptionFreshness from "./components/ReceptionFreshness.jsx";
import ReceptionNotifyModal from "./components/ReceptionNotifyModal.jsx";
import ReceptionLockScreen from "./components/ReceptionLockScreen.jsx";
import ReceptionWalkInModal from "./components/ReceptionWalkInModal.jsx";
import {
  CheckInVisit_api,
  CheckOutVisit_api,
  CreateWalkIn_api,
  NotifyHost_api,
  SetVisitVip_api,
  UnlockReception_api,
  fetchAllBoardRows,
  fetchReceptionBoard,
  fetchReceptionSites,
  isSuccessfulResponse,
  toUiResponse,
} from "./ReceptionAPI.jsx";
import { newIdempotencyKey } from "../Visitor/visitorApiMapper.js";
import ronspotBlueLogo from "../../assets/images/ronspot-blue-logo.svg";
import { buildCsv, downloadCsv, exportFileName } from "./receptionExport.js";
import {
  DEFAULT_PER_PAGE,
  RECEPTION_FILTER,
  getRelativeDay,
} from "./receptionConstants.js";
import useVisitorAccess from "../Visitor/useVisitorAccess.js";

// Same reasoning as the visitor list: one request per letter is several round trips for one
// intention. A front desk types names constantly, so this matters more here, not less.
const SEARCH_DEBOUNCE_MS = 350;

// The site the desk last used. A receptionist opens this screen every morning and it is always
// the same building — making them re-pick it daily would be a chore the screen could have
// remembered. Per-browser, which is per-desk, which is the right scope.
const SITE_STORAGE_KEY = "ronspot.reception.siteId";

// The idle timeout is NOT ours to choose any more.
//
// §7b (A22) added `settings.inactivity_lock_minutes` to the board and asked us plainly to "stop
// hardcoding it" — an admin sets it between 1 and 480 minutes, and a timeout the screen invents is
// one they cannot change. The mapper supplies 30 only when the field is absent entirely.

const readStoredSite = () => {
  try {
    return localStorage.getItem(SITE_STORAGE_KEY) || null;
  } catch {
    // Private mode, or storage blocked by policy. Not worth failing a page load over.
    return null;
  }
};

const storeSite = (siteId) => {
  try {
    if (siteId) localStorage.setItem(SITE_STORAGE_KEY, siteId);
  } catch {
    /* ignore — see readStoredSite */
  }
};

const ReceptionUI = () => {
  const { t } = useTranslation("common");
  const { canReception } = useVisitorAccess();
  const { handleApiUiResponse, renderApiUi } = useApiUiResponse(t);

  const [sites, setSites] = useState([]);
  // Starts NULL, never from storage.
  //
  // Seeding it from localStorage meant the board request fired with the remembered id before the
  // site list had come back to validate it — which is how a fixture id left over from the demo
  // data reached the real API and came back "That site could not be found". The stored value is
  // read below, once there is a list to check it against.
  const [siteId, setSiteId] = useState(null);

  const [board, setBoard] = useState(null);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Null until the first board answers. Sending no date lets the server default to today (§3),
  // which is the right question to ask — the browser does not know what day it is where the site
  // is, and this screen may well be in a different timezone from the server.
  const [date, setDate] = useState(null);
  const [filter, setFilter] = useState(RECEPTION_FILTER.ALL);
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [page, setPage] = useState(1);
  // Whichever date the server last told us was today (§3 returns `is_today` on every board). The
  // first load defaults to today, so this is known from the first response onwards.
  //
  // Kept so the date bar and the stat tenses never have to ask the BROWSER what day it is: that is
  // a different machine’s clock, it can be a day out, and `is_today` exists precisely so we do not
  // have to guess.
  const [serverToday, setServerToday] = useState(null);

  const [selectedId, setSelectedId] = useState(null);
  // Remembers a deliberate close of the detail panel, so the auto-select further down does not
  // immediately undo it.
  //
  // STATE, not a ref. As a ref, clearing it on a new query changed nothing the auto-select effect
  // was watching, so the panel stayed shut until something else happened to re-run the effect.
  const [panelDismissed, setPanelDismissed] = useState(false);

  useEffect(() => {
    document.title = `${t("react_reception_title")} | Ronspot`;
  }, [t]);

  // ── Sites ──────────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!canReception) return;

    let cancelled = false;

    (async () => {
      try {
        const { ok, response, sites: list } = await fetchReceptionSites();
        if (cancelled) return;

        if (!ok) {
          // Surfaced, not swallowed. Without the site list there is no board to draw, and a
          // silent failure here is what let a stale id reach the server and come back as a
          // confusing "site could not be found".
          handleApiUiResponse(response, { successToaster: 0, errorToaster: 1, alertPopup: 1 });
          setSiteId(null);

          return;
        }

        setSites(list);

        const remembered = readStoredSite();

        // THE REMEMBERED SITE IS ONLY A SUGGESTION. It is validated against the list the server
        // actually returned before it is used — a desk that browsed the demo data has a fixture
        // id in localStorage, and sending that to the real API is exactly the 404 we hit.
        //
        // Falling back to the first site rather than leaving the board unscoped: `site_id` is
        // required on every call, so "no site" is not a state this screen can render.
        setSiteId(
          remembered && list.some((site) => site.id === remembered)
            ? remembered
            : list[0]?.id ?? null
        );
      } catch (error) {
        console.error("GetReceptionSites error", error);
        // Same reasoning as the refusal above — a thrown request must not leave a stale id live.
        if (!cancelled) setSiteId(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canReception]);

  useEffect(() => storeSite(siteId), [siteId]);

  // ── The board ──────────────────────────────────────────────────────────────────────────────
  //
  // The in-flight token is the same guard the visitor list uses: a receptionist changes site,
  // date, tab and search constantly, and those responses can land out of order. Without it, a
  // slow answer for yesterday can overwrite today's board — on the screen that says who is in the
  // building.
  const requestToken = useRef(0);


  const loadBoard = useCallback(
    async ({ nextPage = 1, append = false, ...params }) => {
      if (!params.siteId) return;

      const token = ++requestToken.current;

      try {
        if (append) setIsLoadingMore(true);
        else setIsLoading(true);

        const { ok, response, board: loaded } = await fetchReceptionBoard({
          SiteID: params.siteId,
          Date: params.date,
          Filter: params.filter,
          Query: params.term,
          Page: nextPage,
          PerPage: DEFAULT_PER_PAGE,
        });

        if (token !== requestToken.current) return;

        if (!ok) {
          handleApiUiResponse(response, { successToaster: 0, errorToaster: 1, alertPopup: 1 });
          return;
        }

        setBoard(loaded);
        if (loaded.isToday && loaded.date) setServerToday(loaded.date);
        setRows((current) => (append ? [...current, ...loaded.items] : loaded.items));
        setPage(loaded.page);
      } catch (error) {
        console.error("GetReceptionBoard error", error);
      } finally {
        if (token === requestToken.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    // `handleApiUiResponse` is stable for the lifetime of the hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Clearing the box is "show me everyone again" and is applied immediately; anything else waits
  // for the typing to stop.
  useEffect(() => {
    const term = query.trim();

    if (term === "") {
      setAppliedQuery("");
      return undefined;
    }

    const timer = setTimeout(() => setAppliedQuery(term), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  // One effect owns "something about the query changed, start again from page 1". Site, date,
  // filter and search are four narrowing conditions on ONE request, so they share one trigger
  // rather than each firing their own fetch and racing.
  useEffect(() => {
    if (!canReception || !siteId) return;

    setSelectedId(null);
    setPanelDismissed(false);
    loadBoard({ siteId, date, filter, term: appliedQuery, nextPage: 1, append: false });
  }, [canReception, siteId, date, filter, appliedQuery, loadBoard]);

  const hasMore = board?.hasMore === true;

  const loadMore = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMore) return;

    loadBoard({
      siteId,
      date,
      filter,
      term: appliedQuery,
      nextPage: page + 1,
      append: true,
    });
  }, [isLoading, isLoadingMore, hasMore, loadBoard, siteId, date, filter, appliedQuery, page]);

  // ── Writes (§6, §7, §8) ────────────────────────────────────────────────────────────────────
  //
  // Every write carries an `Idempotency-Key`, minted per ACTION. §8 puts it plainest for this
  // screen: "there is a person standing there waiting" — so a receptionist who sees no instant
  // reply presses again, and without a key that registers two people.
  //
  // The key is minted at the press rather than held per flow (as the invite wizard does), because
  // each press here IS the whole action: there is no multi-step form to retry within.
  const [isBusy, setIsBusy] = useState(false);
  const [notifyRow, setNotifyRow] = useState(null);

  const runWrite = useCallback(
    async (label, call) => {
      try {
        setIsBusy(true);

        const response = await call(newIdempotencyKey());

        // The success toast is ON for these. Unlike the invite flows, nothing navigates after a
        // check-in — the board just changes underneath — so without a toast the receptionist has
        // no confirmation that anything happened. A 409's reason (the contractor gate) reaches
        // them through the same helper as an alert popup, which is what §6 asks for: "show these,
        // don't summarise them".
        // `toUiResponse` rewrites the walk-in endpoint’s 201 to a 200 for this helper only —
        // it branches on `code === 200` exactly, and would otherwise show an error toast for a
        // walk-in that registered perfectly well.
        handleApiUiResponse(toUiResponse(response), {
          successToaster: 1,
          errorToaster: 1,
          alertPopup: 1,
        });

        if (!isSuccessfulResponse(response)) return false;

        // Re-read rather than patching the row locally: a check-in moves counts, tabs and the
        // overstay flag, and reconstructing all of that on the client is how the board drifts
        // away from the server.
        await loadBoard({ siteId, date, filter, term: appliedQuery, nextPage: 1, append: false });

        return true;
      } catch (error) {
        console.error(`${label} error`, error);

        return false;
      } finally {
        setIsBusy(false);
      }
    },
    [handleApiUiResponse, loadBoard, siteId, date, filter, appliedQuery]
  );

  const checkIn = useCallback(
    (row) =>
      runWrite("CheckInVisit", (key) =>
        CheckInVisit_api({ VisitID: row.id, SiteID: siteId, IdempotencyKey: key })
      ),
    [runWrite, siteId]
  );

  const checkOut = useCallback(
    (row) =>
      runWrite("CheckOutVisit", (key) =>
        CheckOutVisit_api({ VisitID: row.id, SiteID: siteId, IdempotencyKey: key })
      ),
    [runWrite, siteId]
  );

  const toggleVip = useCallback(
    (row) =>
      runWrite("SetVisitVip", (key) =>
        SetVisitVip_api({
          VisitID: row.id,
          SiteID: siteId,
          // Sent explicitly rather than as a toggle the server works out: re-flagging an
          // already-flagged VIP is a 200 saying so (§7), which is only harmless because the
          // intended state travels with the request.
          IsVip: !row.isVip,
          IdempotencyKey: key,
        })
      ),
    [runWrite, siteId]
  );

  const sendNotification = useCallback(
    async ({ row, channel, message, subject }) => {
      const ok = await runWrite("NotifyHost", (key) =>
        NotifyHost_api({
          VisitID: row.id,
          SiteID: siteId,
          Channel: channel,
          Message: message,
          Subject: subject,
          IdempotencyKey: key,
        })
      );

      // Left open on failure, with the text intact — retyping a message because a send failed is
      // a small insult at a busy desk.
      if (ok) setNotifyRow(null);
    },
    [runWrite, siteId]
  );

  // ── The lock (§7b, R4) ─────────────────────────────────────────────────────────────────────
  //
  // §9: "the PIN lock itself is now built ... What is still open is the *session*: how long an
  // unlock lasts before the screen relocks is your call, on your side." So the idle timeout is
  // ours, and 30 minutes matches the prototype.
  //
  // The PIN itself is NEVER checked here — see ReceptionLockScreen.jsx.
  const [isLocked, setIsLocked] = useState(false);
  const [lockError, setLockError] = useState(null);
  const [lockedForSeconds, setLockedForSeconds] = useState(0);

  const currentSite = useMemo(
    () => sites.find((site) => site.id === siteId) || null,
    [sites, siteId]
  );

  // Whether a PIN exists that will open this desk.
  //
  // Since A22 the PIN is the COMPANY's shared staff PIN, not the site's, and a paired Android
  // kiosk can change it — so it can change without anyone touching this screen. The board's copy
  // is therefore the fresher one: it arrives on every poll, where the sites call answered once at
  // login. The site's flag is the fallback for an API that has not shipped A22 yet.
  //
  // With no PIN there is nothing that could unlock the screen, so locking it would strand the
  // desk — the control is refused rather than offered and then found useless.
  const canLock = board?.settings?.receptionPinSet ?? currentSite?.receptionPinSet === true;

  const lockNow = useCallback(() => {
    if (!canLock) return;

    setLockError(null);
    setLockedForSeconds(0);
    setIsLocked(true);
  }, [canLock]);

  const unlock = useCallback(
    async (pin) => {
      try {
        setIsBusy(true);

        const response = await UnlockReception_api({ SiteID: siteId, Pin: pin });

        if (isSuccessfulResponse(response)) {
          setIsLocked(false);
          setLockError(null);
          setLockedForSeconds(0);

          return;
        }

        // The server's wording, shown as-is. A 403 reads the same whether the PIN was wrong or
        // the site has none — it deliberately never says which.
        setLockError(response?.Data?.ErrorMessage || t("react_reception_lock_failed"));
        // Present only on the 409 after five wrong attempts.
        setLockedForSeconds(Number(response?.Data?.Records?.locked_for_seconds) || 0);
      } catch (error) {
        console.error("UnlockReception error", error);
        setLockError(t("react_reception_lock_failed"));
      } finally {
        setIsBusy(false);
      }
    },
    [siteId, t]
  );

  // Idle auto-lock. The listeners are passive and the timer is only reset, never re-created per
  // event, so a desk screen left scrolling does not churn.
  useEffect(() => {
    if (!canReception || !canLock || isLocked) return undefined;

    const idleMs = (board?.settings?.idleLockMinutes ?? 30) * 60 * 1000;
    let timer = setTimeout(lockNow, idleMs);

    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(lockNow, idleMs);
    };

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((event) => document.addEventListener(event, reset, { passive: true }));

    return () => {
      clearTimeout(timer);
      events.forEach((event) => document.removeEventListener(event, reset));
    };
  }, [canReception, canLock, isLocked, lockNow, board?.settings?.idleLockMinutes]);


  const [isWalkInOpen, setIsWalkInOpen] = useState(false);

  const registerWalkIn = useCallback(
    async (fields) => {
      const ok = await runWrite("CreateWalkIn", (key) =>
        CreateWalkIn_api({
          ...fields,
          SiteID: siteId,
          // The board's date, which is always today while the button is available — see the
          // `is_today` guard on the control itself.
          Date: board?.date,
          IdempotencyKey: key,
        })
      );

      // Left open on failure with the fields intact: re-typing a walk-in's details because a
      // send failed, while they stand at the desk, is the worst moment to lose a form.
      if (ok) setIsWalkInOpen(false);
    },
    [runWrite, siteId, board]
  );

  // ── Auto-selecting the first row ───────────────────────────────────────────────────────────
  //
  // The panel is a third of the screen; leaving it reading "select a visitor" on load wastes it,
  // and the prototype opens with the first row selected.
  //
  // But it must not FIGHT the receptionist. Closing the panel is a deliberate act — usually to
  // see the full-width board — and a re-selection a tick later would make the close button look
  // broken. So a dismissal is remembered until the query changes, at which point the rows on
  // screen are different and the old intent no longer applies.

  const dismissPanel = useCallback(() => {
    setPanelDismissed(true);
    setSelectedId(null);
  }, []);

  useEffect(() => {
    if (panelDismissed || selectedId || !rows.length) return;

    setSelectedId(rows[0].id);
  }, [rows, selectedId, panelDismissed]);

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectedId) || null,
    [rows, selectedId]
  );

  const siteName = useMemo(
    () => board?.site?.name || sites.find((site) => site.id === siteId)?.name || "",
    [board, sites, siteId]
  );

  // ── Export (R5) ────────────────────────────────────────────────────────────────────────────
  //
  // Walks every page matching the current date, filter and search, rather than exporting the rows
  // on screen. Exporting the visible page would hand somebody a file that looks complete and is
  // not — the same failure as a muster that stops at 200.
  const [isExporting, setIsExporting] = useState(false);

  const exportBoard = useCallback(async () => {
    if (!siteId || isExporting) return;

    try {
      setIsExporting(true);

      const { ok, response, rows: allRows, complete } = await fetchAllBoardRows({
        SiteID: siteId,
        Date: date,
        Filter: filter,
        Query: appliedQuery,
      });

      if (!ok) {
        handleApiUiResponse(toUiResponse(response), {
          successToaster: 0,
          errorToaster: 1,
          alertPopup: 1,
        });

        return;
      }

      downloadCsv(
        exportFileName({ siteName, date: board?.date || date, filter }),
        buildCsv(allRows)
      );

      // Said out loud rather than shipped as a short file. `complete` is false only if the page
      // guard tripped, which would mean a paging bug — but a truncated export that says nothing
      // is worse than one that does.
      if (!complete) {
        handleApiUiResponse(
          { Data: { ResponseCode: 400, ErrorMessage: t("react_reception_export_truncated") } },
          { successToaster: 0, errorToaster: 1, alertPopup: 0 }
        );
      }
    } catch (error) {
      console.error("Export error", error);
    } finally {
      setIsExporting(false);
    }
    // `handleApiUiResponse` is stable for the lifetime of the hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, date, filter, appliedQuery, siteName, board, isExporting, t]);

  // ── Access gate ────────────────────────────────────────────────────────────────────────────
  //
  // `canReception` is one of the five host capabilities (§3 of the invite contract) and this
  // screen is behind it. Default is OFF, so an unknown entitlement shows the unavailable state
  // rather than a board the server would refuse anyway.
  if (!canReception) {
    return (
      <div className="reception-page reception-page--closed">
        <p className="reception-unavailable">{t("react_reception_unavailable")}</p>
      </div>
    );
  }

  return (
    <div className="reception-page">
      {/* The capped, centred card. The overlays below are siblings of it, not children, so a
          dialog is never clipped by the card’s width. */}
      <div className="reception-app">
        <ReceptionTopBar
        sites={sites}
        siteId={siteId}
        onSiteChange={setSiteId}
        // The board’s own counts, so the current site’s row shows real numbers rather than a
        // placeholder — they are already on screen in the tiles above.
        counts={board?.counts}
        // R4. Rendered but inert until `POST /visitor/reception/unlock` is wired — the PIN has to
        // be checked server-side, because the browser still holds a valid session while the
        // screen is locked, so a check done in JavaScript is one a passer-by skips by reloading.
        canLock={canLock}
        onLock={lockNow}
      />

      <main className="reception-main">
        <ReceptionDateBar
          date={board?.date || date}
          isToday={board?.isToday ?? false}
          todayISO={serverToday}
          onDateChange={setDate}
        />

        <ReceptionStats
          counts={board?.counts}
          isLoading={isLoading && !board}
          relativeDay={getRelativeDay(board?.date, board?.isToday, serverToday)}
        />

        <section className="reception-controls">
          <ReceptionFilterTabs
            filter={filter}
            counts={board?.counts}
            onFilterChange={setFilter}
          />

          <div className="reception-search">
            {/* Decorative — the placeholder already says what the box is for. */}
            <svg
              className="reception-search__icon"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="text"
              className="reception-search__input"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("react_reception_search_placeholder")}
              aria-label={t("react_reception_search_placeholder")}
            />
            {query.trim() !== appliedQuery && (
              <span className="reception-search__status">{t("react_loading")}</span>
            )}
          </div>

          {/* Only on today's board. A walk-in registers AND checks somebody in (§5) — they are
              standing at the desk — so registering one against yesterday or next Tuesday would
              put a person on site on a day they were not there, in the record that doubles as
              the fire log. Disabled rather than hidden, with the reason in the tooltip, so the
              control's absence is never mistaken for a rendering fault. */}
          {/* Exports everything matching the current date, filter and search — not the rows on
              screen. Built in the browser, so it is NOT the audited export §9 lists as still
              parked; the button says “Download CSV” rather than claiming more than it does. */}
          <button
            type="button"
            className="reception-btn reception-btn--ghost"
            disabled={isExporting || !rows.length}
            onClick={exportBoard}
          >
            {!isExporting && (
              <svg
                className="reception-btn__icon"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            )}
            {t(isExporting ? "react_loading" : "react_reception_export")}
          </button>

          <button
            type="button"
            className="reception-btn reception-btn--accent"
            disabled={!board?.isToday || isBusy}
            title={board?.isToday ? undefined : t("react_reception_walk_in_today_only")}
            onClick={() => setIsWalkInOpen(true)}
          >
            {t("react_reception_walk_in_button")}
          </button>
        </section>

        {/* The board and the detail panel side by side, as the prototype has them. The panel
            renders the row the list already holds — §4 is explicit that it is the same shape, so
            selecting somebody costs no second request. */}
        <div className="reception-split">
          <ReceptionList
            rows={rows}
            isLoading={isLoading}
            // The row menu runs the same handlers the detail panel does, so an action taken from
            // either place behaves identically — including the board re-read afterwards.
            isBusy={isBusy}
            onCheckIn={checkIn}
            onCheckOut={checkOut}
            onNotifyHost={setNotifyRow}
            onToggleVip={toggleVip}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            onLoadMore={loadMore}
            query={query}
            selectedId={selectedId}
            onSelect={(row) => setSelectedId(row.id)}
            siteName={siteName}
            total={board?.total ?? 0}
          />

          <ReceptionDetailPanel
            row={selectedRow}
            isBusy={isBusy}
            onClose={dismissPanel}
            onCheckIn={checkIn}
            onCheckOut={checkOut}
            onToggleVip={toggleVip}
            onNotifyHost={setNotifyRow}
          />
        </div>
      </main>


      <footer className="reception-foot">
        {/* Vendored, like the white one in the top bar — same reasoning: a desk screen that runs
            all day should not depend on a third-party fetch for its own branding. This is the
            blue variant (#1B468B, the same value as --r-blue), for the white footer. */}
        <span className="reception-foot__brand">
          <img className="reception-foot__logo" src={ronspotBlueLogo} alt="Ronspot" />
          {t("react_reception_footer")}
        </span>
        {/* `as_at` is the server's clock at the read — nothing is cached, and §9 says to poll for
            now, so the desk needs to be able to see how stale the board is. */}
        <ReceptionFreshness asAt={board?.asAt} />
      </footer>
      </div>

      <ReceptionNotifyModal
        row={notifyRow}
        isBusy={isBusy}
        onSend={sendNotification}
        onClose={() => setNotifyRow(null)}
      />

      <ReceptionWalkInModal
        isOpen={isWalkInOpen}
        isBusy={isBusy}
        onSubmit={registerWalkIn}
        onClose={() => setIsWalkInOpen(false)}
      />

      {/* Last, so it paints over everything including the open dialogs. */}
      <ReceptionLockScreen
        isLocked={isLocked}
        siteName={siteName}
        error={lockError}
        lockedForSeconds={lockedForSeconds}
        isBusy={isBusy}
        onUnlock={unlock}
      />

      {renderApiUi()}
    </div>
  );
};

export default ReceptionUI;
